import React, { createContext, useContext, useState, useEffect } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { createTheme } from '@mui/material/styles';
import { useAuth } from '../hooks/useAuth';

const ThemeContext = createContext();

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useThemeContext must be used within ThemeProvider');
  return context;
};

// Light theme
const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#1976d2' },
    secondary: { main: '#9c27b0' },
    background: { default: '#f5f5f5', paper: '#ffffff' },
  },
  shape: { borderRadius: 12 },
});

// Dark theme
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#90caf9' },
    secondary: { main: '#ce93d8' },
    background: { default: '#121212', paper: '#1e1e1e' },
  },
  shape: { borderRadius: 12 },
});

export const ThemeProvider = ({ children }) => {
  const { user, updateUserSettings } = useAuth();
  const [mode, setMode] = useState(() => {
    const saved = localStorage.getItem('themeMode');
    return saved || user?.settings?.theme || 'light';
  });

  useEffect(() => {
    localStorage.setItem('themeMode', mode);
    if (user && user.settings?.theme !== mode) {
      updateUserSettings({ theme: mode }).catch(console.error);
    }
  }, [mode, user, updateUserSettings]);

  const toggleTheme = () => setMode(prev => prev === 'light' ? 'dark' : 'light');
  const theme = mode === 'light' ? lightTheme : darkTheme;

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme }}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
};